import { CalendarToday, ExpandMore, History as HistoryComponent, Person } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import { format } from 'date-fns'

import { getFormattedDate } from '../../utils'

import { formatHistoryValue, snakeToText } from './formaters.ts'

export type HistoryRecord = {
  changedFields: Record<string, unknown>
  changedDate: string
  changedBy: string
}

export type Props = {
  history?: HistoryRecord[]
  isLoading?: boolean
  title: string
}

export const History = ({ isLoading, history, title }: Props) => {
  return (
    <Box sx={{ mt: 3 }}>
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box display="flex" alignItems="center" gap={2}>
            <HistoryComponent color="primary" />
            <Typography variant="h6" color="primary">
              {title}
            </Typography>
            {history && (
              <Chip
                label={`${history.length} change${history.length !== 1 ? 's' : ''}`}
                size="small"
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          {(() => {
            if (isLoading) {
              return (
                <Box display="flex" alignItems="center" gap={1} py={2}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    Loading changelog...
                  </Typography>
                </Box>
              )
            }

            if (!history || history?.length === 0) {
              return (
                <Typography variant="body2" color="text.secondary" py={2}>
                  No changes recorded.
                </Typography>
              )
            }

            return (
              <Stack spacing={2}>
                {history.map((entry: HistoryRecord, index: number) => {
                  const changedDate = entry.changedDate
                    ? format(new Date(entry.changedDate), 'MMM dd, yyyy HH:mm')
                    : 'Unknown date'
                  const changedFields = Object.entries(entry.changedFields ?? {})

                  return (
                    <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap" mb={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <CalendarToday sx={{ fontSize: 16 }} color="action" />
                          <Typography variant="caption" color="text.secondary">
                            {getFormattedDate(changedDate)}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Person sx={{ fontSize: 16 }} color="action" />
                          <Typography variant="caption" color="text.secondary">
                            By: {entry.changedBy || 'system'}
                          </Typography>
                        </Box>
                      </Box>
                      <Stack spacing={0.5}>
                        {changedFields.map(([field, value]) => (
                          <Box key={field} display="flex" gap={1} flexWrap="wrap">
                            <Typography variant="body2" fontWeight="medium">
                              {snakeToText(field)}:
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {formatHistoryValue(value)}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Paper>
                  )
                })}
              </Stack>
            )
          })()}
        </AccordionDetails>
      </Accordion>
    </Box>
  )
}

export default History
